/**
 * Shared hex-color math for appearance-driven styling. Originally lived inline in
 * `Button.tsx` (which derives its auto-darkened hover shade from `config.bgColor`); pulled out
 * here so the modal's accent-variable derivation (see `./accent.ts`) can reuse the exact same
 * `darken` — and its new sibling `lighten` — instead of a second copy.
 */

/** How much to darken `bgColor` for the auto hover shade when no `hoverBgColor` is set. */
export const HOVER_DARKEN_AMOUNT = 0.12;

/** Parses `#rgb` or `#rrggbb` into 0–255 channels, or `null` if not a hex color. */
export function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;

  let body = m[1];
  // Unreachable given the pattern above (group 1 always participates in a match), but
  // `noUncheckedIndexedAccess` types `m[1]` as possibly `undefined` — narrow explicitly.
  if (!body) return null;
  if (body.length === 3) {
    // Non-null: the `.length === 3` check above guarantees indices 0–2 exist.
    body = body[0]! + body[0]! + body[1]! + body[1]! + body[2]! + body[2]!;
  }
  return {
    r: parseInt(body.slice(0, 2), 16),
    g: parseInt(body.slice(2, 4), 16),
    b: parseInt(body.slice(4, 6), 16),
  };
}

/**
 * Darkens a `#rgb`/`#rrggbb` hex color by `amount` (0–1) per channel — used to derive a
 * hover background when the config doesn't supply an explicit `hoverBgColor`. Non-hex inputs
 * are returned unchanged so an unusual `bgColor` never throws.
 */
export function darken(hex: string, amount: number): string {
  const parsed = parseHex(hex);
  if (!parsed) return hex;

  const factor = 1 - Math.min(Math.max(amount, 0), 1);
  const to2 = (n: number) =>
    Math.round(Math.min(Math.max(n, 0), 255) * factor)
      .toString(16)
      .padStart(2, '0');
  return `#${to2(parsed.r)}${to2(parsed.g)}${to2(parsed.b)}`;
}

/**
 * Lightens a `#rgb`/`#rrggbb` hex color by mixing `amount` (0–1) toward white per channel —
 * the counterpart to `darken`, used to derive the muted/subtle accent tints. Mirrors `darken`'s
 * contract: non-hex inputs are returned unchanged so an unusual `bgColor` never throws.
 */
export function lighten(hex: string, amount: number): string {
  const parsed = parseHex(hex);
  if (!parsed) return hex;

  const factor = Math.min(Math.max(amount, 0), 1);
  const to2 = (n: number) =>
    Math.round(Math.min(Math.max(n + (255 - n) * factor, 0), 255))
      .toString(16)
      .padStart(2, '0');
  return `#${to2(parsed.r)}${to2(parsed.g)}${to2(parsed.b)}`;
}
